import './style.css';
import { Circuit } from './engine/circuit';
import { ELEMENT_SPECS, createElement, type ElementSpec } from './engine/catalog';
import { fmt } from './engine/format';
import { Renderer } from './ui/render';
import { Editor } from './ui/editor';
import { Inspector } from './ui/inspector';
import { Scope } from './ui/scope';
import { decodeShare, makeShareUrl } from './ui/share';
import { GRID, drawElement, elementBounds } from './ui/symbols';
import { EXAMPLES } from './ui/examples';
import {
  downloadCircuit, downloadModule, listSaves, loadAutosave, loadLibrary, loadLocal, loadIntoCircuit,
  deleteLocal, isCircuitFile, removeFromLibrary, saveAutosave, saveLocal, serialize, upsertLibrary,
  type CircuitFile,
} from './ui/storage';
import { SubcktInstance, makeDef, type SubcktDef } from './engine/subckt';
import { instantiateElements, serializeElements, type SerializedElement } from './engine/serial';
import type { Element } from './engine/element';

/* ───────── DOM ───────── */
const $ = <T extends HTMLElement>(id: string): T => {
  const el = document.getElementById(id);
  if (!el) throw new Error(`#${id} 를 찾을 수 없어요`);
  return el as T;
};

const canvas = $<HTMLCanvasElement>('canvas');
const nameInput = $<HTMLInputElement>('circuit-name');
const banner = $('banner');
const statusEl = $('status');
const speedInput = $<HTMLInputElement>('speed');
const speedLabel = $('speed-label');
const runBtn = $<HTMLButtonElement>('btn-run');
const localSelect = $<HTMLSelectElement>('select-local');
const exampleSelect = $<HTMLSelectElement>('select-example');

/* ───────── 코어 ───────── */
const circuit = new Circuit();
const renderer = new Renderer(canvas);
const editor = new Editor(circuit, renderer);
const inspector = new Inspector($('inspector'));
const scope = new Scope($<HTMLCanvasElement>('scope'), $('scope-legend'));
scope.reset();
inspector.onAddChannel = (el, kind) => scope.add(el, kind);
$<HTMLSelectElement>('scope-window').onchange = (e) => {
  scope.windowSec = parseFloat((e.target as HTMLSelectElement).value);
};
$('scope-csv').onclick = () => scope.downloadCsv();
$('scope-clear').onclick = () => scope.clearData();
$('scope-big').onclick = () => document.body.classList.toggle('scope-big');

let running = true;
let speed = 1;
let dirtySinceSave = false;

/* ───────── 팔레트 ───────── */
function paletteIcon(spec: ElementSpec): HTMLCanvasElement {
  const c = document.createElement('canvas');
  const w = 60;
  const h = 32;
  const dpr = window.devicePixelRatio || 1;
  c.width = w * dpr;
  c.height = h * dpr;
  c.style.width = `${w}px`;
  c.style.height = `${h}px`;
  const ctx = c.getContext('2d');
  if (!ctx) return c;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  if (spec.pinCount > 2 || spec.type === 'not') {
    const el = createElement(spec.type, 0, 0, spec.defaultLength, 0);
    const bw = spec.defaultLength * GRID + 8;
    const tall = ['charger', 'dcdc', 'bms', 'opamp', 'dff'];
    const bh = spec.type === 'mcu' ? 170 : tall.includes(spec.type) ? 66 : 46;
    const scale = Math.min((w - 4) / bw, (h - 2) / bh);
    ctx.save();
    ctx.translate(w / 2, h / 2);
    ctx.scale(scale, scale);
    ctx.translate(-(spec.defaultLength * GRID) / 2, 0);
    drawElement(
      { ctx, color: '#b9c6de', selected: false, highlighted: false, showValues: false, running: false },
      el,
    );
    ctx.restore();
    return c;
  }

  const el = spec.pinCount === 1
    ? createElement(spec.type, 0, 0, 0, 1)
    : createElement(spec.type, 0, 0, 2.4, 0);
  if (spec.type === 'switch') el.props.closed = true;

  ctx.save();
  const scale = 0.78;
  ctx.translate(w / 2, h / 2 + (spec.pinCount === 1 ? -6 : 0));
  ctx.scale(scale, scale);
  ctx.translate(spec.pinCount === 1 ? 0 : -1.2 * GRID, 0);
  drawElement(
    { ctx, color: '#b9c6de', selected: false, highlighted: false, showValues: false, running: false },
    el,
  );
  ctx.restore();
  return c;
}

function moduleIcon(def: SubcktDef): HTMLCanvasElement {
  const c = document.createElement('canvas');
  const w = 60;
  const h = 32;
  const dpr = window.devicePixelRatio || 1;
  c.width = w * dpr;
  c.height = h * dpr;
  c.style.width = `${w}px`;
  c.style.height = `${h}px`;
  const ctx = c.getContext('2d');
  if (!ctx) return c;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const el = createElement('subckt', 0, 0, 6, 0) as SubcktInstance;
  el.syncWithDef(def);
  const b = elementBounds(el);
  const scale = Math.min((w - 4) / (b.x2 - b.x1), (h - 2) / (b.y2 - b.y1));
  ctx.save();
  ctx.translate(w / 2, h / 2);
  ctx.scale(scale, scale);
  ctx.translate(-(b.x1 + b.x2) / 2, -(b.y1 + b.y2) / 2);
  el.inner = [createElement('wire', 0, 0, 1, 0)]; // '정의 없음' 표시 숨기기
  drawElement({ ctx, color: '#b9c6de', selected: false, highlighted: false, showValues: false, running: false }, el);
  ctx.restore();
  return c;
}

/** 팔레트에 보일 모듈 목록: 라이브러리 + 지금 회로에 들어있는 정의 */
function paletteModules(): SubcktDef[] {
  const map = new Map<string, SubcktDef>();
  for (const d of circuit.defs.values()) map.set(d.id, d);
  for (const d of loadLibrary()) {
    const cur = map.get(d.id);
    if (!cur || cur.version < d.version) map.set(d.id, d);
  }
  return [...map.values()].sort((a, b) => a.name.localeCompare(b.name, 'ko'));
}

function buildPalette(): void {
  const root = $('palette');
  root.innerHTML = '';
  const groups = new Map<string, ElementSpec[]>();
  for (const spec of ELEMENT_SPECS) {
    if (spec.group.startsWith('_')) continue;
    const list = groups.get(spec.group) ?? [];
    list.push(spec);
    groups.set(spec.group, list);
  }
  for (const [group, specs] of groups) {
    const g = document.createElement('div');
    g.className = 'pal-group';
    const label = document.createElement('div');
    label.className = 'pal-group-label';
    label.textContent = group;
    const items = document.createElement('div');
    items.className = 'pal-items';
    for (const spec of specs) {
      const btn = document.createElement('button');
      btn.className = 'pal-item';
      btn.dataset.type = spec.type;
      btn.appendChild(paletteIcon(spec));
      const name = document.createElement('span');
      name.textContent = spec.label;
      btn.appendChild(name);
      btn.onclick = () => editor.setTool(editor.tool === spec.type ? 'select' : spec.type);
      items.appendChild(btn);
    }
    g.append(label, items);
    if (group === '모듈 만들기') {
      const hint = document.createElement('div');
      hint.className = 'pal-hint';
      hint.innerHTML = '회로에 <b>포트</b>를 놓고 위의 <b>모듈로 만들기</b>를 누르면 부품 하나로 묶여요.';
      g.appendChild(hint);
      g.appendChild(buildModuleGroup());
    }
    root.appendChild(g);
  }
  syncPalette();
}

function buildModuleGroup(): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'pal-modules';
  const label = document.createElement('div');
  label.className = 'pal-group-label';
  label.style.marginTop = '10px';
  label.textContent = '내 모듈';
  wrap.appendChild(label);
  const defs = paletteModules();
  if (defs.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'pal-hint';
    empty.textContent = '아직 없어요.';
    wrap.appendChild(empty);
    return wrap;
  }
  const items = document.createElement('div');
  items.className = 'pal-items';
  for (const def of defs) {
    const tool = `subckt:${def.id}`;
    const btn = document.createElement('div');
    btn.className = 'pal-item module';
    btn.dataset.type = tool;
    btn.title = `${def.name} — 포트 ${def.ports.map((p) => p.name).join(', ')}`;
    btn.appendChild(moduleIcon(def));
    const name = document.createElement('span');
    name.textContent = def.name;
    btn.appendChild(name);
    btn.onclick = () => editor.setTool(editor.tool === tool ? 'select' : tool);

    const tools = document.createElement('div');
    tools.className = 'pal-tools';
    const mk = (text: string, title: string, fn: () => void) => {
      const b = document.createElement('button');
      b.textContent = text;
      b.title = title;
      b.onclick = (e) => {
        e.stopPropagation();
        fn();
      };
      tools.appendChild(b);
    };
    mk('✎', '모듈 편집', () => {
      registerDefWithDeps(def.id);
      enterModuleEdit(def.id);
    });
    mk('⤓', '파일로 내보내기', () => downloadModule(def));
    mk('×', '내 모듈에서 삭제', () => {
      if (!confirm(`"${def.name}" 모듈을 목록에서 지울까요? (이미 놓인 회로에는 남아 있어요)`)) return;
      removeFromLibrary(def.id);
      if (!circuit.usedDefIds().has(def.id)) circuit.defs.delete(def.id);
      buildPalette();
    });
    btn.appendChild(tools);
    items.appendChild(btn);
  }
  wrap.appendChild(items);
  return wrap;
}

/* ───────── 모듈(서브회로) ───────── */

function findDef(id: string): SubcktDef | undefined {
  const inCircuit = circuit.defs.get(id);
  const inLib = loadLibrary().find((d) => d.id === id);
  if (inCircuit && inLib) return inLib.version > inCircuit.version ? inLib : inCircuit;
  return inCircuit ?? inLib;
}

/** 모듈 정의와, 그 안에서 쓰는 다른 모듈 정의까지 회로에 등록 */
function registerDefWithDeps(id: string, seen = new Set<string>()): void {
  if (seen.has(id)) return;
  seen.add(id);
  const def = findDef(id);
  if (!def) return;
  const cur = circuit.defs.get(id);
  if (!cur || cur.version !== def.version) circuit.upsertDef(def);
  for (const e of def.elements) if (e.type === 'subckt') registerDefWithDeps(String(e.props.defId ?? ''), seen);
}

editor.subcktFactory = (defId, x1, y1, x2, y2) => {
  const def = findDef(defId);
  if (!def) return null;
  registerDefWithDeps(defId);
  const el = createElement('subckt', x1, y1, x2, y2) as SubcktInstance;
  el.syncWithDef(def);
  return el;
};

/** 모듈 편집 중인 단계 (중첩 편집 가능) */
interface EditFrame {
  defId: string;
  elements: SerializedElement[];
  name: string;
  view: { ox: number; oy: number; scale: number };
}
const editStack: EditFrame[] = [];

function enterModuleEdit(defId: string): void {
  const def = findDef(defId);
  if (!def) {
    alert('모듈 정의를 찾을 수 없어요');
    return;
  }
  registerDefWithDeps(defId);
  editStack.push({
    defId,
    elements: serializeElements(circuit.elements),
    name: nameInput.value,
    view: { ...renderer.view },
  });
  circuit.elements = instantiateElements(def.elements, true);
  circuit.markDirty();
  circuit.reset();
  scope.reset();
  nameInput.value = def.name;
  editor.setTool('select');
  editor.clearSelection();
  editor.resetHistory();
  inspector.setTarget(null);
  scope.clearData();
  fitView();
  syncEditBar();
}

function finishModuleEdit(save: boolean): void {
  const frame = editStack[editStack.length - 1];
  if (!frame) return;
  let newDef: SubcktDef | undefined;
  if (save) {
    const old = findDef(frame.defId);
    const r = makeDef(circuit.elements, nameInput.value, frame.defId, (old?.version ?? 0) + 1);
    if (!r.def) {
      alert(r.error);
      return;
    }
    if (old?.color) r.def.color = old.color;
    newDef = r.def;
  }
  editStack.pop();
  circuit.elements = instantiateElements(frame.elements, true);
  if (newDef) {
    circuit.upsertDef(newDef);
    upsertLibrary(newDef);
  }
  circuit.markDirty();
  circuit.reset();
  scope.reset();
  nameInput.value = frame.name;
  renderer.view = { ...frame.view };
  editor.clearSelection();
  editor.resetHistory();
  inspector.setTarget(null);
  syncEditBar();
  buildPalette();
  if (newDef) flash(`"${newDef.name}" 모듈을 저장했어요 (v${newDef.version})`);
}

function syncEditBar(): void {
  const bar = $('module-bar');
  const editing = editStack.length > 0;
  bar.classList.toggle('hidden', !editing);
  document.body.classList.toggle('editing-module', editing);
  if (editing) {
    const path = editStack.map((f) => findDef(f.defId)?.name ?? '?').join(' › ');
    $('module-bar-title').textContent = `모듈 편집 중: ${path}`;
  }
}

$('module-done').onclick = () => finishModuleEdit(true);
$('module-cancel').onclick = () => {
  if (!confirm('바꾼 내용을 버리고 돌아갈까요?')) return;
  finishModuleEdit(false);
};

$('btn-make-module').onclick = () => {
  const source: Element[] = editor.selected.size > 0 ? [...editor.selected] : circuit.elements;
  const ports = source.filter((e) => e.type === 'port');
  if (ports.length === 0) {
    alert('먼저 "포트 (단자)" 부품으로 바깥과 연결될 곳을 표시해 주세요.\n(왼쪽 팔레트 → 모듈 만들기 → 포트)');
    return;
  }
  const name = prompt(
    `모듈 이름을 정해주세요 (${editor.selected.size > 0 ? '선택한 ' + source.length + '개 부품' : '회로 전체'}, 포트 ${ports.length}개)`,
    nameInput.value.trim() && nameInput.value !== '새 회로' ? nameInput.value.trim() : '내 모듈',
  );
  if (name === null) return;
  const r = makeDef(source, name);
  if (!r.def) {
    alert(r.error);
    return;
  }
  // 안에서 쓰는 모듈 정의도 라이브러리에 같이 넣어 둔다 (다른 회로에서 꺼내 쓸 수 있게)
  for (const id of new Set(r.def.elements.filter((e) => e.type === 'subckt').map((e) => String(e.props.defId)))) {
    const dep = findDef(id);
    if (dep) upsertLibrary(dep);
  }
  upsertLibrary(r.def);
  buildPalette();
  flash(`"${r.def.name}" 모듈을 만들었어요 — 왼쪽 '내 모듈'에서 꺼내 쓰세요`);
};

$('btn-share').onclick = async () => {
  try {
    const url = await makeShareUrl(currentFile());
    const kb = (url.length / 1024).toFixed(1);
    try {
      await navigator.clipboard.writeText(url);
      flash(`링크를 복사했어요 (${kb} KB) — 붙여넣어 공유하세요`);
    } catch {
      prompt('이 링크를 복사해서 공유하세요', url);
    }
    if (url.length > 60000) alert('회로가 커서 링크가 아주 길어요. 일부 메신저에서는 잘릴 수 있어요 — 파일로 저장해서 보내는 걸 추천해요.');
  } catch (e) {
    alert(`링크를 만들 수 없어요: ${e instanceof Error ? e.message : e}`);
  }
};

$('btn-inspector').onclick = () => {
  const open = document.body.classList.toggle('show-inspector');
  $('btn-inspector').textContent = open ? '✕ 닫기' : '☰ 속성';
};

editor.onOpenModule = (el) => enterModuleEdit(el.defId);
inspector.onOpenModule = (el) => enterModuleEdit(el.defId);

function syncPalette(): void {
  for (const btn of document.querySelectorAll<HTMLElement>('.pal-item')) {
    btn.classList.toggle('active', btn.dataset.type === editor.tool);
  }
  canvas.style.cursor = editor.tool === 'select' ? 'default' : 'crosshair';
}

/* ───────── 파일 ───────── */
function loadFile(file: CircuitFile, resetHistory = true): void {
  editStack.length = 0;
  syncEditBar();
  loadIntoCircuit(circuit, file);
  scope.reset();
  buildPalette();
  nameInput.value = file.name || '새 회로';
  editor.clearSelection();
  if (resetHistory) editor.resetHistory();
  inspector.setTarget(null);
  if (file.view) renderer.view = { ...file.view };
  else fitView();
  dirtySinceSave = false;
}

function currentFile(): CircuitFile {
  if (editStack.length > 0) {
    // 모듈 편집 중이면 바깥(맨 처음) 회로를 저장한다
    const root = editStack[0];
    return {
      ...serialize(circuit, root.name, root.view),
      elements: root.elements,
      subcircuits: [...circuit.defs.values()],
    };
  }
  return serialize(circuit, nameInput.value.trim() || '새 회로', renderer.view);
}

function fitView(): void {
  if (circuit.elements.length === 0) {
    renderer.view = { ox: renderer.width / 2 - 200, oy: 60, scale: 1 };
    return;
  }
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const el of circuit.elements) {
    const b = elementBounds(el);
    minX = Math.min(minX, b.x1 / GRID); maxX = Math.max(maxX, b.x2 / GRID);
    minY = Math.min(minY, b.y1 / GRID); maxY = Math.max(maxY, b.y2 / GRID);
  }
  maxX += 2; // 오른쪽에 붙는 값 라벨(전압계 등)이 잘리지 않게
  const pad = 2;
  const w = (maxX - minX + pad * 2) * GRID;
  const h = (maxY - minY + pad * 2) * GRID;
  const scale = Math.min(1.45, Math.max(0.35, Math.min(renderer.width / w, renderer.height / h)));
  renderer.view.scale = scale;
  renderer.view.ox = renderer.width / 2 - ((minX + maxX) / 2) * GRID * scale;
  renderer.view.oy = renderer.height / 2 - ((minY + maxY) / 2) * GRID * scale;
}

function refreshLocalList(): void {
  const saves = listSaves();
  localSelect.innerHTML = '<option value="">저장된 회로…</option>';
  for (const s of saves) {
    const opt = document.createElement('option');
    opt.value = s.name;
    opt.textContent = `${s.name} · ${new Date(s.savedAt).toLocaleString('ko-KR', { dateStyle: 'short', timeStyle: 'short' })}`;
    localSelect.appendChild(opt);
  }
}

function buildExampleList(): void {
  for (const ex of EXAMPLES) {
    const opt = document.createElement('option');
    opt.value = ex.name;
    opt.textContent = ex.name;
    opt.title = ex.desc;
    exampleSelect.appendChild(opt);
  }
}

/* ───────── 이벤트 ───────── */
$('btn-new').onclick = () => {
  if (circuit.elements.length && !confirm('지금 회로를 지우고 새로 시작할까요?')) return;
  editor.snapshot();
  circuit.clear();
  scope.reset();
  nameInput.value = '새 회로';
  inspector.setTarget(null);
  fitView();
};

$('btn-save').onclick = () => {
  downloadCircuit(currentFile());
  dirtySinceSave = false;
};

/** 회로 파일(.circuit.json) 또는 모듈 파일(.module.json) 열기 */
function pickJson(): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,application/json';
    input.onchange = async () => {
      const f = input.files?.[0];
      if (!f) return reject(new Error('파일을 고르지 않았어요'));
      try {
        resolve(JSON.parse(await f.text()));
      } catch {
        reject(new Error('JSON 파일을 읽을 수 없어요'));
      }
    };
    input.click();
  });
}

$('btn-open').onclick = async () => {
  try {
    const data = await pickJson();
    const mod = data as { format?: string; module?: SubcktDef };
    if (mod?.format === 'circuitlab-module' && mod.module?.id) {
      upsertLibrary(mod.module);
      buildPalette();
      flash(`"${mod.module.name}" 모듈을 '내 모듈'에 추가했어요`);
      return;
    }
    if (!isCircuitFile(data)) throw new Error('CircuitLab 회로/모듈 파일이 아니에요');
    loadFile(data);
  } catch (e) {
    alert(e instanceof Error ? e.message : '파일을 열 수 없어요');
  }
};

$('btn-save-local').onclick = () => {
  const name = (nameInput.value.trim() || '새 회로');
  saveLocal(serialize(circuit, name, renderer.view));
  refreshLocalList();
  localSelect.value = name;
  dirtySinceSave = false;
  flash(`"${name}" 저장했어요`);
};

$('btn-delete-local').onclick = () => {
  const name = localSelect.value;
  if (!name) return;
  if (!confirm(`"${name}" 저장본을 지울까요?`)) return;
  deleteLocal(name);
  refreshLocalList();
};

localSelect.onchange = () => {
  const name = localSelect.value;
  if (!name) return;
  const file = loadLocal(name);
  if (file) loadFile(file);
};

exampleSelect.onchange = () => {
  const ex = EXAMPLES.find((e) => e.name === exampleSelect.value);
  if (!ex) return;
  loadFile(structuredClone(ex.file));
  flash(ex.desc);
  exampleSelect.value = '';
};

runBtn.onclick = () => setRunning(!running);
$('btn-reset').onclick = () => {
  circuit.reset();
  scope.clearData();
};

speedInput.oninput = () => {
  speed = Math.pow(10, parseFloat(speedInput.value));
  speedLabel.textContent = `${speed >= 100 ? speed.toFixed(0) : speed >= 1 ? speed.toFixed(2) : speed.toFixed(3)}×`;
};

const bindToggle = (id: string, key: 'showVoltage' | 'showCurrent' | 'showValues' | 'showHeat'): void => {
  const input = $<HTMLInputElement>(id);
  input.onchange = () => {
    renderer.options[key] = input.checked;
  };
  renderer.options[key] = input.checked;
};
bindToggle('opt-voltage', 'showVoltage');
bindToggle('opt-current', 'showCurrent');
bindToggle('opt-values', 'showValues');
bindToggle('opt-heat', 'showHeat');

nameInput.onchange = () => {
  dirtySinceSave = true;
};

function setRunning(v: boolean): void {
  running = v;
  renderer.options.running = v;
  runBtn.textContent = v ? '⏸ 일시정지' : '▶ 실행';
  runBtn.classList.toggle('primary', !v);
}

let flashTimer = 0;
function flash(msg: string): void {
  banner.textContent = msg;
  banner.classList.remove('hidden');
  banner.style.background = 'rgba(22,101,52,.92)';
  banner.style.borderColor = '#4ade80';
  banner.style.color = '#dcfce7';
  window.clearTimeout(flashTimer);
  flashTimer = window.setTimeout(() => banner.classList.add('hidden'), 2600);
}

function showError(msg: string | null): void {
  if (!msg) return;
  banner.textContent = msg;
  banner.classList.remove('hidden');
  banner.style.background = 'rgba(127,29,29,.92)';
  banner.style.borderColor = '#ef4444';
  banner.style.color = '#fee2e2';
  window.clearTimeout(flashTimer);
  flashTimer = window.setTimeout(() => banner.classList.add('hidden'), 2600);
}

editor.onChange = () => {
  dirtySinceSave = true;
  scope.prune(new Set(circuit.elements));
};
editor.onSelectionChange = () => {
  const sel = [...editor.selected];
  inspector.setTarget(sel.length === 1 ? sel[0] : null);
  scope.setPreview(sel.length === 1 ? sel[0] : null);
};
editor.onToolChange = syncPalette;
inspector.onEdit = () => {
  circuit.markDirty();
  dirtySinceSave = true;
};

/* ───────── 메인 루프 ───────── */
let lastTime = performance.now();
let lastAutosave = 0;

function frame(now: number): void {
  const frameSeconds = Math.min((now - lastTime) / 1000, 0.05);
  lastTime = now;

  if (running && circuit.elements.length > 0) {
    const span = frameSeconds * speed;
    // 한 프레임에 쓸 수 있는 스텝 수 (회로가 크면 줄인다). 모자라면 '실시간보다 느림'
    const budget = Math.max(20, Math.min(2000, Math.round(40000 / (circuit.allElements.length + 20))));
    circuit.advance(span, budget);
    if (!circuit.lastResult.ok) showError(circuit.lastResult.error ?? null);
    renderer.advance(span, circuit);
    scope.sample(circuit.time);
  }

  renderer.render(circuit, {
    selected: editor.selected,
    hover: editor.hover,
    preview: editor.preview,
    rubber: editor.rubber,
    cursorGrid: editor.cursorGrid,
  });
  inspector.refresh();
  scope.draw();

  const st = circuit.stats;
  statusEl.innerHTML =
    `t = ${fmt(circuit.time, 's')} · dt ${fmt(st.lastDt, 's')} · 노드 ${circuit.nodeTotal} · 소자 ${circuit.elements.length}` +
    (running && st.behind ? ' · <span class="warn">실시간보다 느림</span>' : '') +
    (circuit.lastResult.ok ? '' : ` · <span class="err">${circuit.lastResult.error}</span>`);

  if (dirtySinceSave && now - lastAutosave > 3000) {
    saveAutosave(currentFile());
    lastAutosave = now;
  }

  requestAnimationFrame(frame);
}

/* ───────── 시작 ───────── */
function init(): void {
  buildPalette();
  buildExampleList();
  refreshLocalList();
  editor.attach();
  renderer.resize();
  setRunning(true);
  speedInput.dispatchEvent(new Event('input'));

  const saved = loadAutosave();
  if (saved && saved.elements.length > 0) loadFile(saved);
  else loadFile(structuredClone(EXAMPLES[0].file));

  // 공유 링크로 들어왔으면 그 회로를 연다
  if (location.hash.length > 3) {
    void decodeShare(location.hash).then((file) => {
      if (!file) {
        showError('공유 링크가 깨져 있어서 열 수 없어요');
        return;
      }
      loadFile(file);
      flash(`공유받은 회로 "${file.name}" 를 열었어요`);
      history.replaceState(null, '', location.pathname + location.search);
    });
  }

  window.addEventListener('resize', () => renderer.resize());
  window.addEventListener('beforeunload', () => saveAutosave(currentFile()));

  requestAnimationFrame(frame);
}

init();

// 개발용: 브라우저 콘솔에서 __circuitlab.circuit 처럼 내부를 들여다볼 수 있게
(window as unknown as Record<string, unknown>).__circuitlab = { circuit, renderer, editor, inspector };
